import React, { useState } from 'react';
import { WEB3FORMS_ACCESS_KEY } from '../../config/web3forms';
import { BOOKING_URL, BOOKING_ENABLED } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';

/**
 * Drop-in lead-capture block for tool result cards (dark background).
 * - Email capture -> Web3Forms (sends the result summary to BUILDLAB) + GA4 `lead_capture`.
 * - Book-a-consult link -> opens BOOKING_URL + GA4 `book_consult`.
 *
 * Props:
 *   tool      (string)  required — names the tool in the lead email + GA4 event.
 *   summary   (string)  optional — a short readable summary of the user's result.
 *   emailLabel/bookLabel (string) optional — button copy overrides.
 */
const ToolLeadCapture = ({
    tool,
    summary = '',
    emailLabel = 'Email me the breakdown',
    bookLabel = 'Book a 15-min consult',
}) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | submitting | success | error

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setStatus('error'); return; }
        setStatus('submitting');
        try {
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `New lead from ${tool} — BUILDLAB`,
                    from_name: `BUILDLAB · ${tool}`,
                    email,
                    tool,
                    results: summary || '(no summary provided)',
                }),
            });
            const data = await res.json();
            if (data.success) {
                setStatus('success');
                setEmail('');
                trackEvent('lead_capture', { tool, method: 'email_results' });
            } else { setStatus('error'); }
        } catch (err) { setStatus('error'); }
    };

    const inputStyle = {
        flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
        color: '#fff', padding: '10px 12px', fontSize: '.9rem',
    };

    return (
        <div style={{ marginTop: 18, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 16 }}>
            {status === 'success' ? (
                <p style={{ color: '#7CFFB2', fontSize: '.9rem', margin: 0 }}>
                    Thanks — we&rsquo;ll email your detailed breakdown shortly.
                </p>
            ) : (
                <form onSubmit={onSubmit}>
                    <div style={{ fontSize: '.82rem', opacity: 0.85, marginBottom: 8 }}>
                        Want this in writing? We&rsquo;ll send your full breakdown.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            aria-label={`Email address to receive your ${tool} results`}
                            style={inputStyle}
                        />
                        <button type="submit" disabled={status === 'submitting'} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                            <span className="btn_label" data-text={emailLabel}>{status === 'submitting' ? 'Sending…' : emailLabel}</span>
                        </button>
                    </div>
                    {status === 'error' && (
                        <div style={{ color: '#ffb3b3', fontSize: '.8rem', marginTop: 6 }}>Enter a valid email, or try again shortly.</div>
                    )}
                </form>
            )}
            {BOOKING_ENABLED && (
                <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('book_consult', { tool })}
                    style={{ display: 'block', textAlign: 'center', marginTop: 12, color: '#FFC9A3', fontWeight: 600, fontSize: '.9rem', textDecoration: 'none' }}
                >
                    {bookLabel} <i className="fa-solid fa-arrow-up-right" aria-hidden="true" style={{ fontSize: '.72rem' }}></i>
                </a>
            )}
        </div>
    );
};

export default ToolLeadCapture;
